import type {
  PeriodControl,
  PeriodControlPreset,
  PeriodControlPresetValue,
} from 'lib-analytics-types';
import moment from 'moment-timezone';

import { i18n } from 'utils/i18n';

const isPreset = (
  values: PeriodControl['values'],
): values is PeriodControlPresetValue => {
  return (values as PeriodControlPresetValue).length === 1;
};

/**
 * Formats a period either as a preset or a custom date range.
 *
 * @param period The period to format
 * @param timezone The selected timezone
 * @param presets The list of period presets
 */
export function formatAnalyticsPeriod(
  period: PeriodControl,
  timezone: string,
  presets: readonly PeriodControlPreset[],
): string {
  if (isPreset(period.values)) {
    return (
      presets.find(({ value }) => value === period.values[0].value)?.label ?? ''
    );
  }

  const [start, end] = period.values;
  const startDate = moment.tz(start.value, timezone).toDate();
  const endDate = moment.tz(end.value, timezone).toDate();

  const currYear = new Date().getFullYear();
  const isCurrYear =
    startDate.getFullYear() === currYear && endDate.getFullYear() === currYear;

  const intlFormatter = new Intl.DateTimeFormat(i18n.getPreferredLocale(), {
    month: 'short',
    day: 'numeric',
    year: isCurrYear ? undefined : 'numeric',
    timeZone: timezone,
  });

  const startLabel = intlFormatter.format(startDate);
  const endLabel = intlFormatter.format(endDate);

  return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
}
